import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';

const { FiX, FiTwitter, FiFacebook, FiMail, FiClipboard, FiCheck } = FiIcons;

const ShareModal = ({ isOpen, onClose, title = 'Share this content', url }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || window.location.href;

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const socials = [
    {
      name: 'Twitter',
      icon: FiTwitter,
      color: 'bg-[#1DA1F2]/10 text-[#1DA1F2] border-[#1DA1F2]/20',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Check out this awesome content on Vision Direct!')}`,
    },
    {
      name: 'Facebook',
      icon: FiFacebook,
      color: 'bg-[#1877F2]/10 text-[#1877F2] border-[#1877F2]/20',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Email',
      icon: FiMail,
      color: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      url: `mailto:?subject=${encodeURIComponent('Check out this content on Vision Direct')}&body=${encodeURIComponent(`I thought you might be interested in this: ${shareUrl}`)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md glass-strong rounded-[32px] border-2 border-black p-8 space-y-6"
        style={{ boxShadow: '6px 6px 0 #000' }}
      >
        <div className="flex items-start justify-between">
          <h3 className="text-2xl font-black uppercase tracking-tight text-white">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl glass-card flex items-center justify-center text-zinc-400 hover:text-white">
            <SafeIcon icon={FiX} className="text-sm" />
          </button>
        </div>
        
        <div className="flex flex-col gap-3">
          {socials.map(social => (
            <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" 
              className={`flex items-center gap-3 p-4 rounded-2xl border ${social.color} hover:opacity-80 transition-opacity`}>
              <SafeIcon icon={social.icon} className="text-xl" />
              <span className="text-sm font-black">Share on {social.name}</span>
            </a>
          ))}
          
          <button onClick={handleCopy} 
            className="flex items-center justify-between p-4 rounded-2xl bg-brand-pink/10 text-brand-pink border border-brand-pink/20 hover:bg-brand-pink/15 transition-colors">
            <div className="flex items-center gap-3">
              <SafeIcon icon={copied ? FiCheck : FiClipboard} className="text-xl" />
              <span className="text-sm font-black">{copied ? 'Copied to clipboard!' : 'Copy link'}</span>
            </div>
            <span className="text-xs font-mono bg-zinc-900/60 rounded-lg px-2 py-1 border border-white/5 max-w-[200px] truncate">
              {shareUrl}
            </span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ShareModal;